(ns practitest.frontend.users-view.events
  (:require
    [ajax.core :as ajax]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.grid.events :as grid-events]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.grid.subs :as grid-subs]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.users-view.utils :as utils]
    [re-frame.core :as re-frame]))

(defn- account-id [db]
  (get-in db [:current-route :path-params :account-id]))

(re-frame/reg-event-db
 ::initialize
 (fn [db [_ config]]
   (-> db
       (update :progress disj ::refresh-users)
       (update :users-view merge
               {:loading? false
                :users (get config :users [])
                :current-page (get config :current_page 1)
                :per-page (get config :per_page)
                :total-count (get config :total_count 0)
                :full-data? (boolean (get config :full))
                :current-user-id (get config :current_user_id)
                :seat-type-options (get config :seat_type_options [])
                :seats-usage (get config :seats_usage [])
                :pat-for-new-users-supported? (boolean (get config :pat_for_new_users_supported))
                :pat-for-new-users? (boolean (get config :pat_for_new_users))
                :notify-license-change-supported? (boolean (get config :notify_license_change_supported))
                :notify-license-change? (boolean (get config :notify_license_change))
                :all-groups (get config :all_groups [])}))))

(re-frame/reg-event-fx
 ::action-error
 (fn [{:keys [db]} _]
   {:db (-> db
            (assoc-in [:users-view :loading?] false)
            (update :progress disj ::refresh-users))
    :dispatch [::notifications/error2
               (get-translation "sections.notify.error-saving" "Something went wrong. Please try again")]}))

;; The grid filters on rendered column fields; the server wants raw values and its own names.
(defn- server-gridfilter [gridfilter]
  (reduce-kv
    (fn [acc k v]
      (case k
        "name" (assoc acc "name" v)
        "email" (assoc acc "email" v)
        "groups_list" (assoc acc "groups" v)
        "pat_state_label" (assoc acc "pat_state" (mapv utils/pat-label->state v))
        acc))
    {}
    (js->clj (js/JSON.parse gridfilter))))

;; Only these columns can be ordered by server-side; anything else would silently
;; fall back to the server's default order, so drop it rather than mislead the grid.
(def ^:private server-order-by
  {"name" "name"
   "email" "email"
   "project_count" "project_count"
   "pat_state_label" "pat_state"})

(defn- server-order [query]
  (if-let [field (some-> (:order_by query) name)]
    (if-let [mapped (get server-order-by field)]
      (assoc query :order_by mapped)
      (dissoc query :order_by :order_direction))
    query))

(defn- server-query [query]
  (let [query (server-order query)]
    (if-let [gridfilter (:gridfilter query)]
      (let [mapped (server-gridfilter gridfilter)]
        (if (seq mapped)
          (assoc query :gridfilter (js/JSON.stringify (clj->js mapped)))
          (dissoc query :gridfilter)))
      query)))

;; The grid restores per-page/page/sort from storage on mount but does not fire a
;; query of its own, so the route-enter load has to ask for the same slice the grid
;; is about to render - otherwise page 1 of 10 arrives for a grid showing 50 per page.
(defn- initial-query []
  (let [[sort-column direction] (grid-session-storage/load-sort-order utils/table-id)]
    (cond-> {:per_page (or (grid-storage/load-per-page utils/table-id) server/default-per-page)
             :page (or (grid-storage/load-current-page utils/table-id) 1)}
      (some? sort-column)
      (assoc :order_by sort-column :order_direction (name direction)))))

(re-frame/reg-event-fx
 ::refresh-users
 (fn [{:keys [db]} [_ query]]
   (let [query (or query (get-in db [:users-view :query]) (initial-query))]
     {:db (-> db
              (assoc-in [:users-view :query] query)
              (update :progress conj ::refresh-users))
      :http-xhrio {:method :get
                   :uri (broutes/get-url "account/accounts#users_view_data" {:account_id (account-id db)})
                   :params (server-query query)
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::initialize]
                   :on-failure [::action-error]}})))

;; Selection outlives the grid, so drop it whenever the list is re-fetched.
(re-frame/reg-event-fx
 ::reset
 (fn [_ _]
   {:dispatch-n [[::grid-events/clear-selection utils/selection-path]
                 [::refresh-users]]}))

;; Route controller's :start dispatches this (route-enter load, shows the spinner).
;; Post-mutation refreshes dispatch ::refresh-users directly, silently.
(re-frame/reg-event-fx
 ::load!
 (fn [{:keys [db]} _]
   {:db (assoc-in db [:users-view :loading?] true)
    :dispatch [::reset]}))

(re-frame/reg-event-db
 ::open-modal
 (fn [db [_ modal-type row extra-state]]
   (update db :users-view merge (merge {:active-modal modal-type :modal-row row} extra-state))))

(re-frame/reg-event-db
 ::close-modal
 (fn [db _]
   (update db :users-view dissoc :active-modal :modal-row)))

;; Reset password
(re-frame/reg-event-fx
 ::reset-password
 (fn [{:keys [db]} _]
   (let [user-id (:id (get-in db [:users-view :modal-row]))]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#hard_password_reset" {:account_id (account-id db)})
                   :params {:uid user-id}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::reset-password-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::reset-password-success
 (fn [{:keys [db]} [_ response]]
   {:db (update db :users-view dissoc :active-modal :modal-row)
    :dispatch [::notifications/success2
               (or (:message response)
                   (get-translation "users-view.reset-password.success" "Password reset link sent to the user"))]}))

;; Export
(re-frame/reg-event-fx
 ::export-users
 (fn [{:keys [db]} _]
   {:http-xhrio {:method :post
                 :uri (broutes/get-url "account/accounts#users_view_export" {:account_id (account-id db)})
                 :params {}
                 :format (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success [::export-users-success]
                 :on-failure [::action-error]}}))

(re-frame/reg-event-fx
 ::export-users-success
 (fn [_ [_ response]]
   (if (:error response)
     {:dispatch [::notifications/error2 (:error response)]}
     {:dispatch [::notifications/success2
                 (get-translation "users-view.export.success" "Export has begun. You will be notified with an email once it's ready for download.")]})))

;; Rename user
(re-frame/reg-event-db
 ::set-rename-value
 (fn [db [_ value]]
   (assoc-in db [:users-view :rename-value] value)))

(re-frame/reg-event-fx
 ::rename-user
 (fn [{:keys [db]} _]
   (let [row (get-in db [:users-view :modal-row])
         name (get-in db [:users-view :rename-value])]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#rename_user" {:account_id (account-id db)})
                   :params {:user_id (:id row) :name name}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::rename-user-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::rename-user-success
 (fn [{:keys [db]} [_ response]]
   {:db (update db :users-view dissoc :active-modal :modal-row)
    :dispatch-n [[::notifications/success2
                  (or (:message response)
                      (get-translation "users-view.rename.success" "User name updated successfully"))]
                 [::refresh-users]]}))

;; PAT settings

(re-frame/reg-event-db
 ::set-pat-value
 (fn [db [_ value]]
   (assoc-in db [:users-view :pat-value] value)))

(re-frame/reg-event-fx
 ::update-pat
 (fn [{:keys [db]} _]
   (let [row (get-in db [:users-view :modal-row])
         new-pat (get-in db [:users-view :pat-value])]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#update_pat" {:account_id (account-id db)})
                   :params {:user_id (:id row) :new_pat new-pat}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::update-pat-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::update-pat-success
 (fn [{:keys [db]} [_ response]]
   {:db (update db :users-view dissoc :active-modal :modal-row)
    :dispatch-n [[::notifications/success2
                  (or (:message response)
                      (get-translation "users-view.pat.success" "Updated User Personal API Token settings successfully"))]
                 [::refresh-users]]}))

;; Batch PAT settings
(re-frame/reg-event-fx
 ::batch-update-pat
 (fn [{:keys [db]} _]
   (let [user-ids (grid-subs/get-selection db utils/selection-path)
         new-pat (get-in db [:users-view :pat-value])]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#batch_update_pat" {:account_id (account-id db)})
                   :params {:user_ids (vec user-ids) :new_pat new-pat}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::batch-update-pat-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::batch-update-pat-success
 (fn [{:keys [db]} [_ response]]
   (if (:error response)
     {:dispatch [::notifications/error2 (:error response)]}
     {:db (update db :users-view dissoc :active-modal :modal-row)
      :dispatch-n [[::notifications/success2
                    (or (:message response)
                        (get-translation "users-view.pat.success" "Updated User Personal API Token settings successfully"))]
                   [::reset]]})))

;; Remove users
(re-frame/reg-event-fx
 ::remove-users
 (fn [{:keys [db]} [_ user-ids]]
   {:http-xhrio {:method :post
                 :uri (broutes/get-url "account/accounts#batch_delete_users_from_projects"
                                       {:account_id (account-id db)})
                 :params {:selected_users (vec user-ids)}
                 :format (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success [::remove-users-success (count user-ids)]
                 :on-failure [::action-error]}}))

(re-frame/reg-event-fx
 ::remove-users-success
 (fn [{:keys [db]} [_ removed-count]]
   {:db (update db :users-view dissoc :active-modal :modal-row)
    :dispatch-n [[::notifications/success2
                  (if (= 1 removed-count)
                    (get-translation "users-view.remove-user.success"
                                     "User was removed from all projects successfully")
                    (get-translation "users-view.remove-users.success"
                                     "Users were removed from all projects successfully"))]
                 [::reset]]}))

;; Account ownership
(re-frame/reg-event-fx
 ::add-owner
 (fn [{:keys [db]} _]
   (let [user-id (:id (get-in db [:users-view :modal-row]))]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#add_owner" {:account_id (account-id db)})
                   :params {:user_id user-id}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::owner-change-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::remove-owner
 (fn [{:keys [db]} _]
   (let [user-id (:id (get-in db [:users-view :modal-row]))]
     {:http-xhrio {:method :post
                   :uri (broutes/get-url "account/accounts#remove_owner" {:account_id (account-id db)})
                   :params {:user_id user-id}
                   :format (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success [::owner-change-success]
                   :on-failure [::action-error]}})))

(re-frame/reg-event-fx
 ::owner-change-success
 (fn [{:keys [db]} [_ response]]
   (if (:error response)
     {:dispatch [::notifications/error2 (:error response)]}
     {:db (update db :users-view dissoc :active-modal :modal-row)
      :dispatch-n [[::notifications/success2 (:message response)]
                   [::refresh-users]]})))

;; Footer settings
(re-frame/reg-event-fx
 ::toggle-account-setting
 (fn [{:keys [db]} [_ state-key route value]]
   {:http-xhrio {:method :post
                 :uri (broutes/get-url route {:account_id (account-id db)})
                 :params {:value (str value)}
                 :format (ajax/json-request-format)
                 :response-format (ajax/json-response-format {:keywords? true})
                 :on-success [::toggle-account-setting-success state-key value]
                 :on-failure [::action-error]}}))

(re-frame/reg-event-fx
 ::toggle-account-setting-success
 (fn [{:keys [db]} [_ state-key value]]
   {:db (assoc-in db [:users-view state-key] value)
    :dispatch [::notifications/success2
               (get-translation "users-view.settings.success" "Settings updated successfully")]}))
